import React, { useState, useRef } from 'react'
import { OmForm, OmFormItem, OmButton, OmIcon } from '@owma/kit'
import CalendarDatePicker from 'pages/todo/components/DatePicker'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

import i18n from 'i18n'
import styles from './ScheduleSend.module.scss'
import calendarIcon from 'assets/images/calendar/icon-calendar-24.svg'
import CustomTimeSelector from 'pages/calendar/components/EventEditor/CustomTimeSelector'
import { format12Hours } from 'pages/calendar/components/EventEditor/timeFormats'

// dayjs 플러그인 등록
dayjs.extend(utc)
dayjs.extend(timezone)

interface ScheduleSendProps {
  initDateTime: Dayjs
  onDateTimeChange?: (dateTime: Dayjs | null, isValid: boolean) => void
  onPreviewEML?: () => void
}

const ScheduleSend: React.FC<ScheduleSendProps> = ({
  initDateTime,
  onDateTimeChange,
  onPreviewEML,
}) => {
  const [selectedDate, setSelectedDate] = useState<Dayjs>(initDateTime)
  const [selectedTime, setSelectedTime] = useState<Dayjs>(initDateTime)
  const [datePickerOpen, setDatePickerOpen] = useState<boolean>(false)

  const handleDateChange = (date: Dayjs): void => {
    setSelectedDate(date)
    updateDateTime(date, selectedTime)
  }

  const handleTimeChange = (time: string): void => {
    const [hours, minutes] = time.split(':').map(Number)
    const newTime = selectedTime.set('hour', hours).set('minute', minutes)
    setSelectedTime(newTime)
    updateDateTime(selectedDate, newTime)
  }

  const updateDateTime = (date: Dayjs, time: Dayjs): void => {
    if (date && time) {
      const combinedDateTime = date.hour(time.hour()).minute(time.minute()).second(0)

      // 현재 시간보다 이전인지 확인
      const isValid = !combinedDateTime.isBefore(dayjs())

      if (onDateTimeChange) {
        onDateTimeChange(isValid ? combinedDateTime : null, isValid)
      }
    } else {
      if (onDateTimeChange) {
        onDateTimeChange(null, false)
      }
    }
  }

  const eventEditData = {
    startDate: selectedTime.toDate(),
    endDate: selectedTime.toDate(),
    startTz: 'Asia/Seoul',
  }

  const eventData = useRef<any>(eventEditData)

  const startTimeSelectorProps = {
    timing: 'start' as const,
    onTimeChange: handleTimeChange,
    editEventData: eventData.current,
    defaultValue: format12Hours(selectedTime.toDate(), 'Asia/Seoul'),
  }

  return (
    <div className={styles.scheduleSend}>
      <div className={styles.description}>{i18n.t('scheduleSend.desc1')}</div>

      <OmForm layout="horizontal" labelCol={{ span: 6 }} wrapperCol={{ span: 18 }}>
        <OmFormItem className={styles.inputSection} label={i18n.t('scheduleSend.sendAt')}>
          <div className={styles.inputGroup}>
            <div className={styles.datePickerContainer}>
              <button
                className={styles.dateButton}
                onClick={() => setDatePickerOpen(!datePickerOpen)}
              >
                <span className={styles.dateValue}>
                  {selectedDate ? selectedDate.format('YYYY-MM-DD') : 'Select date'}
                </span>
                <img src={calendarIcon} alt="Calendar" className={styles.chevronIcon} />
              </button>
              {datePickerOpen && (
                <div className={styles.datePickerOverlay} onClick={() => setDatePickerOpen(false)}>
                  <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                    <CalendarDatePicker
                      value={selectedDate ? selectedDate.toDate() : null}
                      onChange={(date: Date) => {
                        handleDateChange(dayjs(date))
                        setDatePickerOpen(false)
                      }}
                      onClose={() => setDatePickerOpen(false)}
                    />
                  </div>
                </div>
              )}
            </div>
            <CustomTimeSelector {...startTimeSelectorProps} />
          </div>
        </OmFormItem>
        <OmFormItem className={styles.timezoneInfo} label={i18n.t('scheduleSend.timeZone')}>
          <span className={styles.timezoneText}>(GMT+09:00) Asia/Seoul</span>
        </OmFormItem>
      </OmForm>

      {onPreviewEML && (
        <div className={styles.previewSection}>
          <OmButton
            variant="default"
            icon={<OmIcon name="eye" />}
            onClick={onPreviewEML}
            className={styles.previewButton}
          >
            Preview EML
          </OmButton>
        </div>
      )}

      <div className={styles.infoSection}>
        <ul className={styles.infoList}>
          <li>{i18n.t('scheduleSend.desc2')}</li>
          <li>{i18n.t('scheduleSend.desc3')}</li>
        </ul>
      </div>
    </div>
  )
}

export default ScheduleSend
